/** Repositório TAX: todas as operações enviadas para aprovação, de todos os usuários. */

import { FilterX, Search } from "lucide-react";
import * as React from "react";

import { ETAPAS } from "@/components/aprovacoes/SeloAprovacao";
import { TabelaEnvios } from "@/components/aprovacoes/TabelaEnvios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useRepositorioAprovacoes } from "@/hooks/queries";
import type { EnvioAprovacao, TipoAprovacao } from "@/types";

type EstadoFiltro = "todos" | "aprovado" | "reprovado" | "pendente";

const ESTADOS: { valor: EstadoFiltro; rotulo: string }[] = [
  { valor: "todos", rotulo: "Todos" },
  { valor: "aprovado", rotulo: "Aprovado" },
  { valor: "reprovado", rotulo: "Reprovado" },
  { valor: "pendente", rotulo: "Pendente" },
];

const CATEGORIAS = ["Produtora", "ECE", "Distribuidora"];
const TODAS = "todas";

const SEM_FILTRO: Record<TipoAprovacao, EstadoFiltro> = {
  contabil: "todos",
  tax: "todos",
  negocio: "todos",
};

function estadoDe(envio: EnvioAprovacao, tipo: TipoAprovacao): EstadoFiltro {
  return envio.aprovacoes[tipo]?.decisao ?? "pendente";
}

export function RepositorioTax() {
  const { data: envios, isLoading } = useRepositorioAprovacoes();
  const [busca, setBusca] = React.useState("");
  const [filtros, setFiltros] = React.useState(SEM_FILTRO);
  const [categoria, setCategoria] = React.useState(TODAS);
  const [uf, setUf] = React.useState(TODAS);

  const ufs = [...new Set((envios ?? []).map((e) => e.uf).filter(Boolean))].sort();

  const termo = busca.trim().toLowerCase();
  const filtrando =
    termo !== "" ||
    categoria !== TODAS ||
    uf !== TODAS ||
    ETAPAS.some((e) => filtros[e.tipo] !== "todos");
  const visiveis = (envios ?? []).filter(
    (envio) =>
      (!termo ||
        [envio.nome, envio.categoria, envio.autor ?? ""].some((campo) =>
          campo.toLowerCase().includes(termo),
        )) &&
      (categoria === TODAS || envio.categoria === categoria) &&
      (uf === TODAS || envio.uf === uf) &&
      ETAPAS.every(
        ({ tipo }) => filtros[tipo] === "todos" || estadoDe(envio, tipo) === filtros[tipo],
      ),
  );

  return (
    <Card>
      <CardHeader className="gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>Repositório</CardTitle>
            <CardDescription className="tabular-nums">
              {envios &&
                (filtrando
                  ? `${visiveis.length} de ${envios.length} operações`
                  : `${envios.length} ${envios.length === 1 ? "operação" : "operações"}`)}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Buscar por nome, categoria ou autor…"
                aria-label="Buscar operações"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-x-3 gap-y-2 rounded-lg border border-border/70 bg-muted/30 p-3">
          <label className="grid gap-1">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
              Categoria
            </span>
            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger className="h-8 w-36 bg-card" aria-label="Filtrar por categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas</SelectItem>
                {CATEGORIAS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="grid gap-1">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
              UF
            </span>
            <Select value={uf} onValueChange={setUf}>
              <SelectTrigger className="h-8 w-36 bg-card" aria-label="Filtrar por UF">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas</SelectItem>
                {ufs.map((u) => (
                  <SelectItem key={u} value={u}>
                    {u}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <div className="ml-auto flex flex-wrap items-end gap-3 sm:border-l sm:border-border/70 sm:pl-3">
            {ETAPAS.map(({ tipo, rotulo }) => (
              <label key={tipo} className="grid gap-1">
                <span className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-muted-foreground">
                  {rotulo}
                </span>
                <Select
                  value={filtros[tipo]}
                  onValueChange={(valor) =>
                    setFiltros((atual) => ({
                      ...atual,
                      [tipo]: valor as EstadoFiltro,
                    }))
                  }
                >
                  <SelectTrigger className="h-8 w-36 bg-card" aria-label={`Aprovação ${rotulo}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((estado) => (
                      <SelectItem key={estado.valor} value={estado.valor}>
                        {estado.rotulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            ))}
          </div>
          <Button
            className="self-end"
            size="sm"
            variant="ghost"
            disabled={!filtrando}
            onClick={() => {
              setBusca("");
              setFiltros(SEM_FILTRO);
              setCategoria(TODAS);
              setUf(TODAS);
            }}
          >
            <FilterX />
            Limpar filtros
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading || !envios ? (
          <Skeleton className="h-40 w-full" />
        ) : visiveis.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {filtrando ? "Nenhuma operação com esses filtros." : "Nenhuma operação enviada ainda."}
          </p>
        ) : (
          <TabelaEnvios
            envios={visiveis}
            mostrarAutor
            origem={{
              para: "/repositorio",
              rotulo: "Repositório",
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
